import { FocusEventHandler, ReactElement, ReactNode, useRef, useState } from 'react';
import RSelect, {
  MultiValueProps,
  InputProps as RSelectInputProps,
  components,
} from 'react-select';

import { FormError } from '@components/form-error';
import { IconProps } from '@components/icons';
import { IconCloseSmall } from '@components/icons/close-small';
import { IconFormIndicatorLocked } from '@components/icons/form-indicator-locked';
import { IconReturn } from '@components/icons/return';
import { useSelectTranslations } from '@components/select/locale';
import { Tooltip } from '@components/tooltip';
import { useEvent } from '@hooks/use-event';

import {
  FocusIconContainer,
  HelpText,
  InputBox,
  OptionContainer,
  StyledMultiValue,
  styles,
} from './styles';

import { calculateTextWidth, useAriaId, useIsInsideModalOrSidebar } from '../component-helpers';
import { FormLabel } from '../form-label';
import { Stack } from '../stack';

export interface SelectOption {
  label: string | ReactNode;
  value: string;
  helpText?: string;
  isDisabled?: boolean;
  tooltip?: string;
}

export interface SelectMultiProps {
  options: SelectOption[];
  value?: SelectOption[];
  defaultValue?: SelectOption | SelectOption[];
  onChange: (selectedOptions: SelectOption[]) => void;
  onBlur?: FocusEventHandler;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  labelInfo?: string;
  placeholder?: string;
  noOptions?: (obj: { inputValue: string }) => string | null;
  error?: string;
  menuPortalTarget?: HTMLElement;
  isDisabled?: boolean;
}

const Option = (props: any) => {
  const content = (
    <FocusIconContainer>
      <OptionContainer $helpText={props.data.helpText}>
        {props.children}
        {props.data.helpText && <HelpText>{props.data.helpText}</HelpText>}
      </OptionContainer>
      {props.isFocused && <IconReturn />}
    </FocusIconContainer>
  );
  return props.data.tooltip ? (
    <components.Option {...props}>
      <Tooltip placement="left" content={props.data.tooltip}>
        {content}
      </Tooltip>
    </components.Option>
  ) : (
    <components.Option {...props}>{content}</components.Option>
  );
};

export function SelectMulti(props: SelectMultiProps) {
  const { t } = useSelectTranslations();
  const {
    options,
    value,
    defaultValue,
    onBlur,
    onChange,
    label,
    labelIcon,
    labelInfo,
    placeholder = t('placeholder'),
    noOptions = () => t('no_options'),
    error,
    isDisabled = false,
  } = props;
  const selectId = useAriaId('selectmulti');
  const ref = useRef<HTMLDivElement | null>(null);
  const isInsideModalOrSidebar = useIsInsideModalOrSidebar(ref);

  const isControlled = value !== undefined;

  const [localSelected, setLocalSelected] = useState<SelectOption[]>(() => {
    if (!defaultValue) return [];

    if (Array.isArray(defaultValue)) return defaultValue;

    return [defaultValue];
  });

  const selectedValue = isControlled ? (Array.isArray(value) ? value : [value]) : localSelected;

  const handleChange = useEvent((selected: SelectOption[]) => {
    onChange(selected);
    setLocalSelected(selected);
  });

  return (
    <Stack space={1} ref={ref}>
      <FormLabel text={label} htmlFor={selectId} icon={labelIcon} info={labelInfo} />
      <RSelect
        isDisabled={isDisabled}
        value={selectedValue}
        id={selectId}
        onBlur={onBlur}
        defaultValue={defaultValue}
        onChange={handleChange}
        menuPlacement="auto"
        openMenuOnFocus
        noOptionsMessage={noOptions}
        menuShouldBlockScroll={isInsideModalOrSidebar}
        menuPortalTarget={props.menuPortalTarget ?? isInsideModalOrSidebar ? document.body : null}
        isMulti
        closeMenuOnSelect={options.length === 1}
        styles={styles}
        isClearable={false}
        placeholder={placeholder}
        options={options}
        error={error}
        components={{
          IndicatorSeparator: () => null,
          Option,
          // Remove the dropdown indicator for multi, only show lock if disabled
          DropdownIndicator: isDisabled ? LockIndicator : null,
          // Remove the clear indicator for multi
          ClearIndicator: null,
          MultiValue,
          // Need custom input because the auto-grow built-in thing is broken (sigh)
          Input: CustomInput,
        }}
      />
      <FormError text={error} />
    </Stack>
  );
}

function CustomInput(props: RSelectInputProps) {
  let {
    innerRef,
    cx,
    getStyles,
    theme,
    isHidden,
    isDisabled,
    // @ts-ignore
    value,
    ...rest
  } = props;
  return (
    <InputBox
      // @ts-ignore stfu
      ref={innerRef}
      style={{
        width: calculateTextWidth(value, "normal 400 16px 'Work Sans'"),
      }}
      value={value}
      {...rest}
    />
  );
}

const LockIndicator = (props: any) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <IconFormIndicatorLocked />
      </components.DropdownIndicator>
    )
  );
};

function MultiValue({ children, removeProps, data }: MultiValueProps<any>) {
  return (
    <StyledMultiValue {...removeProps} $color={data.color ?? ''}>
      <span>{children}</span>
      <IconCloseSmall />
    </StyledMultiValue>
  );
}
